import { useCallback } from "react"
import { download, generateCsv, mkConfig } from "export-to-csv"
import { DateTime } from "luxon"
import { ZReportFlat } from "../zReportsTypes"
import { MonetaryAmount } from "../../../../../currency/currencyTypes"
import { toCurrency, toDinero } from "../../../../../currency/currencyLib"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useMoney } from "../../../../../currency/useMoney"
import { useDateTime } from "../../../../../localization/useDateTime"

type ExportCsvParams = Readonly<{
    data: ZReportFlat[] | null | undefined
    startDate: DateTime
    endDate: DateTime
}>

export function useExportZReports({ data, startDate, endDate }: ExportCsvParams) {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({ locale: "da-DK" })

    const { l10n } = useAppLocalization()

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ",",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-Z-reports-${dateTimeFactory.formatDate(startDate)}_${dateTimeFactory.formatDate(endDate)}`,
    })

    function getCurrencyString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const currency = toCurrency(amount.currency)
        return moneyFactory.getLocalizedCurrencySymbol(currency)
    }

    function getAmountString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const dinero = toDinero(amount)
        return moneyFactory.formatAsNumber(dinero)
    }

    const mapOrderToCsvRow = (order: ZReportFlat) => {
        return {
            Date: order?.date ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(order.date)) : "",
            "Pos device": order?.device?.name ?? "",
            ["Amount " + `(${getCurrencyString(order?.sales)})`]: getAmountString(order?.sales),
        }
    }

    return useCallback(() => {
        let exportRows: ReturnType<typeof mapOrderToCsvRow>[]

        if (data && data.length > 0) {
            exportRows = data.map((zReport) => mapOrderToCsvRow(zReport))
        } else {
            exportRows = [mapOrderToCsvRow({} as ZReportFlat)]
        }

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, moneyFactory, data, startDate, endDate, l10n])
}
